import { useEffect, useState } from 'react';
import { createKeydownHandler } from '../../../helpers';
import { Heading } from '../../heading';
import { weekDayNames } from '../constants';
import { WeekDayBarProps, WeekNavType } from '../types';
import { formatDate, getMonthsNames, getNextSevenDays, getPrevSevenDays, getWeekStartDay } from '../utils';
import { useWeekViewDatePicker } from '../week-view-date-picker.provider';
import { Navigate, WeekDay } from '.';

export const WeekDayBar = ({ disabledDays = [], disablePastDates = true }: WeekDayBarProps) => {
  const { activeValue, onChange } = useWeekViewDatePicker();
  const weekStartDay = getWeekStartDay(activeValue.date);
  const [days, setDays] = useState(getNextSevenDays(weekStartDay));
  const [monthName, setMonthName] = useState<string>('');
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const keyDownHandler = createKeydownHandler({
    orientation: 'horizontal',
    parentSelector: '.week-day-bar',
    siblingSelector: '[role="listitem"]',
  });

  const handleNavigateClick = (type: WeekNavType) => {
    if (type === 'next') {
      const lastDayOfTheWeek = new Date(days[6]!);
      setDays(getNextSevenDays(new Date(lastDayOfTheWeek.setDate(lastDayOfTheWeek.getDate() + 1))));
    } else {
      setDays(getPrevSevenDays(days[0]!));
    }
  };

  const handleWeekDayClick = (date: Date) => {
    onChange({ ...activeValue, date: formatDate(date) });
  };

  useEffect(() => {
    setMonthName(getMonthsNames(days));
  }, [days]);

  return (
    <>
      <Heading className='week-view-heading' level={2}>
        {monthName}
      </Heading>
      <div className='week-day-bar'>
        <Navigate onClick={handleNavigateClick} type='previous' />
        <div aria-label='Week date picker' className='week-day-list' role='list'>
          {days.map((date) => {
            const name = weekDayNames[date.getDay()];
            if (!name) {
              return null;
            }
            return (
              <WeekDay
                ariaLabel={`${date.toLocaleDateString()}`}
                date={date.getDate()}
                disabled={disabledDays.indexOf(name) > -1 || (disablePastDates && date < today)}
                isToday={formatDate(date) === formatDate(today)}
                key={name}
                name={name}
                onClick={() => handleWeekDayClick(date)}
                onKeyDown={keyDownHandler}
                selected={new Date(activeValue.date || '').toDateString() === date.toDateString()}
              />
            );
          })}
        </div>
        <Navigate onClick={handleNavigateClick} type='next' />
      </div>
    </>
  );
};
